
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from "@/hooks/use-toast";
import { LocationAutocomplete } from '@/components/LocationAutocomplete';
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface SearchFormProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  locationQuery: string;
  setLocationQuery: (location: string) => void;
  radius: number;
  setRadius: (radius: number) => void;
  useRadius: boolean;
  setUseRadius: (useRadius: boolean) => void;
  onSearch: (e?: React.FormEvent) => Promise<void>;
  isSearching: boolean;
  hasSearched: boolean;
  onClearSearch: () => void;
}

const SearchForm: React.FC<SearchFormProps> = ({
  searchQuery,
  setSearchQuery,
  locationQuery,
  setLocationQuery,
  radius,
  setRadius,
  useRadius,
  setUseRadius,
  onSearch,
  isSearching,
  hasSearched,
  onClearSearch
}) => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Auto-collapse on mobile after search
  useEffect(() => {
    if (hasSearched && isMobile) {
      setIsCollapsed(true);
    }
  }, [hasSearched, isMobile]);
  
  const formatRadius = (meters: number) => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} km`;
    }
    return `${meters} m`;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery && !locationQuery) {
      toast({
        variant: "destructive",
        title: "Search Error",
        description: "Please enter a search term or location.",
      });
      return;
    }
    
    onSearch(e);
  };

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <CardTitle>Search for Businesses</CardTitle>
            <CardDescription className={cn(isCollapsed && "md:block hidden")}>
              Enter a business type or name and location to find leads
            </CardDescription>
          </div>
          {hasSearched && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="md:hidden flex-shrink-0 ml-2"
              type="button"
            >
              {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </Button>
          )}
        </div>
      </CardHeader>
      
      <Collapsible open={!isCollapsed}>
        <CollapsibleContent>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <div className="relative">
              <Search className="absolute top-3 left-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search for businesses (e.g., restaurants, plumbers, lawyers)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div>
            <LocationAutocomplete
              value={locationQuery}
              onChange={setLocationQuery}
              placeholder="Location (e.g., Cork City, Ireland or specific address)"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Enter a specific location to enable location-based search
            </p>
          </div>
          
          <div className="flex items-center space-x-2 mb-3">
            <Switch 
              id="use-radius" 
              checked={useRadius} 
              onCheckedChange={setUseRadius}
            />
            <Label htmlFor="use-radius">Use radius-based search</Label>
            <p className="text-xs text-muted-foreground ml-2">
              {useRadius 
                ? "Search within a specific radius from the location" 
                : "Search broadly across the location (better for country-wide searches)"}
            </p>
          </div>
          
          <div className={useRadius ? "" : "opacity-50"}>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Search Radius: {formatRadius(radius)}
            </label>
            <input
              type="range"
              min="1000"
              max="50000"
              step="1000"
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
              disabled={!useRadius}
            />
          </div>
          
          <div className="flex gap-2">
            <Button type="submit" disabled={isSearching} className="flex-1">
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
            {hasSearched && (
              <Button type="button" variant="outline" onClick={onClearSearch}>
                Clear
              </Button>
            )}
          </div>
        </form>
      </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default SearchForm;
